<template>
  <b-container fluid id="app" style="background-color: transparent;">
    <DriverContacts v-if="this.$route.fullPath.startsWith('/driver-contacts')"/>
    <BusTracker v-else-if="this.$route.fullPath.startsWith('/bus-tracker')"/>
    <ArrDepBoard v-else-if="this.$route.fullPath.startsWith('/arr-dep-board')"/>
    <ArrDepBoardLg v-else-if="this.$route.fullPath.startsWith('/arr-dep-lg-board')"/>
    <SOSDetail v-else-if="this.$route.fullPath.startsWith('/sos_map')"/>
    <SosDispatch v-else-if="this.$route.path && this.$route.path.endsWith('/dispatcher')"/>
    <VehicleMapDirect v-else-if="this.$route.path === '/map'"/>
    <VehicleMapDriver v-else-if="this.$route.fullPath.startsWith('/drivermap')"/>
    <VehicleMapTrack v-else-if="this.$route.fullPath.startsWith('/vehicle-track')"/>
    <VehicleMapTrackFleetFabbus v-else-if="this.$route.fullPath.startsWith('/fleet-track-fabbus')"/>
    <VehicleMapTrackFleet v-else-if="this.$route.fullPath.startsWith('/fleet-track')"/>
    <VehicleMapTrackBooking v-else-if="this.$route.fullPath.startsWith('/booking-track')"/>
    <!-- Map Link Builder: shareable public live-tracking link. chromeless
         and unauth by design (same as the booking-track map above) - the
         person clicking it may have no fleet-int account at all. -->
    <VehicleMapLink v-else-if="this.$route.fullPath.startsWith('/map-link/')"/>
    <!-- bus sos yard-only self-registration: must sit above the bare
         /register prefix match or it would render the old form -->
    <SosRegister v-else-if="this.$route.fullPath.startsWith('/register/sos')"/>
    <RegisterDirect v-else-if="this.$route.fullPath.startsWith('/register')"/>
    <VehStatusTBNDVIR v-else-if="this.$route.fullPath.startsWith('/status')"/>
    <VehStatusMaintTBN v-else-if="this.$route.fullPath.startsWith('/vsm-maint-tbn')"/>
    <VehStatusMaint v-else-if="this.$route.fullPath.startsWith('/vsm-maint')"/>
    <VehStatusUpcomingTBN v-else-if="this.$route.fullPath.startsWith('/vsm-upcoming-tbn')"/>
    <VehStatusUpcoming v-else-if="this.$route.fullPath.startsWith('/vsm-upcoming')"/>
    <VehMaintSummary v-else-if="this.$route.fullPath.startsWith('/veh-maint-summary')"/>
    <VehDefects v-else-if="this.$route.fullPath.startsWith('/veh-defects')"/>
    <VehStatusTBN v-else-if="this.$route.fullPath.startsWith('/vsm-tbn')"/>
    <VehStatusFasttrakDVIR v-else-if="this.$route.fullPath.startsWith('/vsm-fasttrak')"/>
    <PlannedVehicleTypesMonthlyTBN v-else-if="this.$route.fullPath.startsWith('/vehicle-util-monthly-tbn')"/>
    <PlannedDriverTypesMonthlyTBN v-else-if="this.$route.fullPath.startsWith('/driver-util-monthly-tbn')"/>
    <PlannedDriverTypesMonthlyCM v-else-if="this.$route.fullPath.startsWith('/driver-util-monthly-cm')"/>
    <VehStatus v-else-if="this.$route.fullPath.startsWith('/vsm-v1')"/>
    <VehStatusES v-else-if="this.$route.fullPath.startsWith('/vsm-es')"/>
    <!-- standalone vsm2: must sit above the bare /vsm catch-all or it would
         silently render the legacy VehStatusDVIR. the login guard's
         startsWith('/vsm') bypass covers it; the board reads ?tenant= and
         ?src= itself (see vsm_board's standalone computed) -->
    <VsmBoard v-else-if="this.$route.fullPath.startsWith('/vsm2')"/>
    <VehStatusDVIR v-else-if="this.$route.fullPath.startsWith('/vsm')"/>
    <BookingCustomerFleetMap v-else-if="this.$route.fullPath.startsWith('/booking-customer-map')"/>
    <!-- FI-1813 public ops desk submission form. when not authenticated,
         render the form chromeless (no navbar, no FA, no footer) so
         customers landing from the published /op/<slug> link see only
         the form. when authenticated, fall through to the regular
         router-view branch below so the form sits inside the normal
         app chrome — staff can preview the form without losing their
         tenant context. -->
    <OpsDeskPublicForm v-else-if="!authenticated && this.$route.fullPath.startsWith('/op/')"/>
    <!-- public driver SOS form; chromeless for anonymous drivers, staff see
         it inside normal chrome via the router-view branch below. -->
    <SosDriverForm v-else-if="!authenticated && (this.$route.path === '/sos' || this.$route.fullPath.startsWith('/sos/'))"/>
    <!-- FI-1869 horizon nav: theme-gated chrome (site_theme 'new' or
         'midnight'). the
         classic branch below stays byte-for-byte so classic carries zero
         risk; the duplication is confined to this template. -->
    <div v-else-if="authenticated && horizonNav" class="fi-hz-root">
      <FiTopNav @authenticated="setAuthenticated" />
      <!-- FiFavoritesBar owns --fi-hz-top-offset itself while mounted (its
           height varies: the row wraps on desktop and scrolls sideways as a
           single row on mobile) -- see its syncTopOffset -->
      <FiFavoritesBar v-if="showFavoritesBar" />
      <div class="fi-hz-body" :class="{ 'fi-hz-body--rail-collapsed': fiNavState.collapsed }">
        <FiSectionNav />
        <main class="fi-hz-content">
          <!-- banners live inside the content pane so the fixed rail never
               overlaps them -->
          <ServiceAlertBanner />
          <FiUpdateNudge />
          <b-alert v-model="alert.show" :variant="alert.variant" dismissible fade id="root-alert">
            {{alert.message}}
          </b-alert>
          <keep-alive>
            <router-view v-if="$route.meta.keepAlive" @authenticated="setAuthenticated" @showAlert="showAlert" />
          </keep-alive>
          <router-view v-if="!$route.meta.keepAlive" @authenticated="setAuthenticated" @showAlert="showAlert" />
        </main>
      </div>
      <!-- bar-launches: on phone widths the horizon top bar carries the FA
           button, so the launcher hides its floating pill there -->
      <FleetAssistantLauncher v-if="faLauncherOn" bar-launches @showAlert="showAlert" />
      <AiUsageRequestModal @showAlert="showAlert" />
      <!-- a support ticket opened from anywhere (a notification, the
           dashboard widget) lands here, over whatever page is showing,
           instead of routing away from it. see utils/support_ticket_links -->
      <SupportTicketModal ref="globalTicketModal" modal-id="app-ticket-modal"
                          @hidden="onGlobalTicketHidden" @showAlert="showAlert" />
    </div>
    <div v-else-if="authenticated">
      <navbar @authenticated="setAuthenticated" v-if="authenticated"/>
      <!-- FI-1432: site-wide banner for active service alerts. self-gates on
           the vuex notification list so no render cost when there's no alert. -->
      <ServiceAlertBanner />
      <!-- FI-2181: calm "a new version is ready" strip, self-gating -->
      <FiUpdateNudge />
      <b-alert v-model="alert.show" :variant="alert.variant" dismissible fade id="root-alert">
        {{alert.message}}
      </b-alert>
<!--      <div v-if="telematics_type==='SAMSARA'" style="background-color: darkgoldenrod; font-size: 20px; font-weight: bold; color: white">THERE WAS A SAMSARA OUTAGE ON 2/23 THAT MAY HAVE IMPACTED YOUR ACCOUNT - <a target="_blank" href="https://www.samsarastatus.com/#">DETAILS HERE</a></div>-->

      <keep-alive>
        <router-view v-if="$route.meta.keepAlive" @authenticated="setAuthenticated" @showAlert="showAlert" />
      </keep-alive>
      <router-view v-if="!$route.meta.keepAlive" @authenticated="setAuthenticated" @showAlert="showAlert" />
      <FleetAssistantLauncher v-if="faLauncherOn" @showAlert="showAlert" />
      <AiUsageRequestModal @showAlert="showAlert" />
      <SupportTicketModal ref="globalTicketModal" modal-id="app-ticket-modal"
                          @hidden="onGlobalTicketHidden" @showAlert="showAlert" />
    </div>
    <div v-else id="login_top" style="min-height:100vh;background-size:contain;overflow:hidden">
      <Login @authenticated="setAuthenticated" @showAlert="showAlert"/>
    </div>
    <!-- classic sunset: the one-time "welcome to horizon" sheet for accounts
         the migration moved. mounted OUTSIDE the theme branches so a theme
         picked inside the sheet (classic included) swaps the shell underneath
         without unmounting the sheet; it self-gates on the user's preferences
         and renders nothing otherwise -->
    <FiClassicSunsetNotice v-if="authenticated" />
  </b-container>
</template>

<script>
import {mapMutations, mapState} from 'vuex'
import { fsSetUser, fsAnonymize } from './utils/fullstory'
import { readPasskeyMarkers, restorePasskeyMarkers } from './utils/passkeys'
import { isChromelessPath } from './utils/chromeless_routes'
import navbar from './components/navbar'
import FiTopNav from './components/nav/fi_top_nav.vue'
import FiSectionNav from './components/nav/fi_section_nav.vue'
import FiFavoritesBar from './components/nav/fi_favorites_bar.vue'
import FiClassicSunsetNotice from './components/nav/fi_classic_sunset_notice.vue'
import { navState as fiNavState } from './nav/nav_state'
import { ensureFavoritesLoaded } from './nav/nav_favorites'
import { faLauncherAvailable } from './nav/registry'
import Login from './components/login'
import SOSDetail from './components/sos_sender_detail'
import SosDispatch from './components/sos_dispatch'
import VehicleMapDirect from './components/vehicle_map_direct'
import VehicleMapDriver from './components/vehicle_map_driver'
import PlannedVehicleTypesMonthlyTBN from './components/planned_vehicle_types_monthly_tbn.vue'
import PlannedDriverTypesMonthlyTBN from './components/planned_driver_types_monthly_tbn.vue'
import PlannedDriverTypesMonthlyCM from './components/planned_driver_types_monthly_cm.vue'
import VehicleMapTrack from './components/vehicle_map_track'
import VehicleMapTrackFleet from './components/vehicle_map_track_fleet'
import VehicleMapTrackFleetFabbus from './components/vehicle_map_track_fleet_fabbus'
import BookingCustomerFleetMap from './components/booking_customer_fleet_map'
import VehicleMapTrackBooking from './components/vehicle_map_by_booking'
import VehicleMapLink from './components/vehicle_map_link'
import RegisterDirect from './components/direct_register'
import SosRegister from './components/sos_register.vue'
import OpsDeskPublicForm from './components/ops_desk_public_form.vue'
import SosDriverForm from './components/sos_driver_form.vue'
import VehStatusTBNDVIR from './components/vehicle_status_dvir_tbn'
import VehStatusFasttrakDVIR from './components/vehicle_status_dvir_fasttrak'
import VehStatusMaintTBN from './components/vehicle_status_maint_tbn'
import VehStatusMaint from './components/vehicle_status_maint'
import VehStatusUpcomingTBN from './components/vehicle_status_upcoming_tbn'
import VehStatusUpcoming from './components/vehicle_status_upcoming'
import VehMaintSummary from './components/vehicle_maint_summary'
import VehDefects from './components/vehicle_defects'
import VehStatusTBN from './components/vehicle_status_tbn'
import VehStatus from './components/vehicle_status'
import VehStatusES from './components/vehicle_status_es'
import VehStatusDVIR from './components/vehicle_status_dvir'
import VsmBoard from './components/vsm_board.vue'
import BusTracker from './components/bus_tracker'
import DriverContacts from './components/driver_contacts'
import ArrDepBoard from './components/arrivals_departures_board'
import ArrDepBoardLg from './components/arrivals_departures_board_large'
import Planned_vehicle_types_monthly_tbn from "./components/planned_vehicle_types_monthly_tbn.vue";
import FleetAssistantLauncher from './components/fleet_assistant_launcher.vue'
import AiUsageRequestModal from './components/ai_usage_request_modal.vue'
import SupportTicketModal from './components/support_ticket_modal.vue'
import ServiceAlertBanner from './components/service_alert_banner.vue'
import FiUpdateNudge from './components/fi_update_nudge.vue'
import { launchTutorial } from './utils/tutorials'
import {
  get_notifications_unread_count as cm_get_unread_count,
  get_user_notifications as cm_get_user_notifications,
  refresh_session,
} from './networking/apis'
// FI-2180: keep the session signed in across releases. the timed renewal
// below trades a jwt in its last four days for a fresh one; the decision
// and the storage writes are pure helpers so they can be tested alone.
import {
  tokenExp, applyRenewedSession, endSessionLocally, renewalPrecheck, renewalFailureAction,
  RENEW_ATTEMPT_KEY, SIGNOUT_KEY,
} from './utils/session_renewal'
import {
  tbn_get_notifications_unread_count,
  tbn_get_user_notifications,
} from './networking/tbn_apis'
// FI-1657: live-push WebSocket client. when VUE_APP_WS_URL is empty (DEV
// or stages where the WS API hasn't been provisioned) we skip WS entirely
// and fall back to the legacy 60s poll cadence, see startNotificationsPolling.
import NotificationsWs from './networking/notifications_ws'

// FI-1657 polling cadence. polling runs at 60s ONLY when the WS isn't
// open. while WS is connected we stop the timer entirely and rely on
// push signals + nc-refresh (dropdown open) + the post-(re)connect
// pollNotifications priming to keep state fresh. on any close we
// immediately restart the 60s timer so a dropped WS is invisible to
// the user.
const POLL_INTERVAL_FAST_MS = 60000

// FI-2180: how often a signed-in tab asks whether its jwt is due for
// renewal. the check itself is cheap and shouldRenewToken only says yes
// inside the token's last four days, at most once per ten minutes.
const SESSION_RENEW_CHECK_MS = 15 * 60 * 1000

// allowlist for Fleet Assistant action buttons ([Label](action:NAME) in markdown).
// the renderer emits any action:NAME the model writes, but ONLY names listed here
// are honored — this object is the security gate (role check + arg whitelist +
// optional confirm). an unknown name no-ops; a name the user lacks the role for
// is marked aria-disabled. keep this in lockstep with the action list taught in
// the backend FA instructions. args are filtered to the declared keys.
//
// each entry declares the roles allowed to run it, the arg keys it accepts
// (everything else is dropped), an optional confirm prompt, and a `run`. a
// plain `run` is a synchronous navigation; set `async: true` when `run` returns
// a promise (a real operation) so handleFaAction shows a busy state on the
// control and a success/error toast. nav actions exist instead of plain
// `[x](/route)` links only when the client must resolve something the model
// can't — the tenant-aware TBN vs CM route, or a role gate that renders the
// control inert for users who can't reach the target.
//
// mutations like `generate_payroll_report` are intentionally absent until their
// endpoint + UX are wired and signed off — the backend prompt must not advertise
// an action that isn't here, or it renders inert.
const FA_ACTION_ALLOWLIST = {
  open_payroll_report: {
    roles: ['admin', 'customer-admin', 'manager', 'dispatch'],
    args: ['date'],
    run(args, vm) {
      // resolve the TBN vs standard route the model can't know about; pass the
      // date through so the report can honor it where supported.
      const name = vm.isTbnTenant() ? 'actuals_driver_payroll_report_tbn' : 'actuals_driver_payroll_report'
      const query = args.date ? { date: args.date } : {}
      vm.$router.push({ name, query }).catch(() => {})
    },
  },
  open_departures: {
    // arrivals/departures board — single route, no params.
    roles: ['admin', 'customer-admin', 'manager', 'dispatch'],
    args: [],
    run(args, vm) {
      vm.$router.push({ name: 'arrivals_departures_board' }).catch(() => {})
    },
  },
  open_driver: {
    // driver detail — TBN vs CM split; both read the driver via query.id.
    roles: ['admin', 'customer-admin', 'manager', 'dispatch'],
    args: ['id'],
    run(args, vm) {
      if (!args.id) return
      const name = vm.isTbnTenant() ? 'driver_detail_tbn' : 'driver_detail'
      vm.$router.push({ name, query: { id: args.id } }).catch(() => {})
    },
  },
  open_booking: {
    // booking detail — no dedicated route; open the actuals list filtered to
    // this booking. TBN vs CM split; both read query.booking_id.
    roles: ['admin', 'customer-admin', 'manager', 'dispatch'],
    args: ['id'],
    run(args, vm) {
      if (!args.id) return
      const name = vm.isTbnTenant() ? 'actuals_tbn' : 'actuals'
      vm.$router.push({ name, query: { booking_id: args.id } }).catch(() => {})
    },
  },
  open_vehicle_history: {
    // vehicle GPS/route history — single route, reads query.vehicleId.
    roles: ['admin', 'customer-admin', 'manager', 'dispatch'],
    args: ['id'],
    run(args, vm) {
      if (!args.id) return
      vm.$router.push({ name: 'vehicle_route_history', query: { vehicleId: args.id } }).catch(() => {})
    },
  },
}

export default {
  name: 'App',
  metaInfo: {
          title: "Fleet Integrations",
          titleTemplate: "Fleet Integrations",
  },
  data() {
    return {
      authenticated: false,
      alert: {
        show: false,
        message: '',
        variant: ''
      },
      // horizon nav collapse state; drives the content pane's left margin
      fiNavState,
    }
  },
  components: {
    Planned_vehicle_types_monthly_tbn,
    navbar, FiTopNav, FiSectionNav, FiFavoritesBar, FiClassicSunsetNotice, Login, SOSDetail, SosDispatch, VehicleMapDirect, VehicleMapDriver, VehicleMapTrack, VehicleMapTrackFleet, VehicleMapTrackFleetFabbus, BookingCustomerFleetMap, VehicleMapTrackBooking, VehicleMapLink, RegisterDirect, SosRegister, OpsDeskPublicForm, VehStatusTBNDVIR, VehStatusFasttrakDVIR, VehStatusTBN, VehStatusDVIR, VsmBoard, VehStatusMaintTBN, VehStatusMaint, VehStatusUpcomingTBN, VehStatusUpcoming, VehStatus, VehStatusES, VehMaintSummary, VehDefects, BusTracker, ArrDepBoard, ArrDepBoardLg, PlannedVehicleTypesMonthlyTBN, PlannedDriverTypesMonthlyTBN, PlannedDriverTypesMonthlyCM, FleetAssistantLauncher, AiUsageRequestModal, SupportTicketModal, ServiceAlertBanner, FiUpdateNudge, DriverContacts, SosDriverForm
  },
  created() {
    // iOS 17+ interactive-widget hint: tells Safari to resize the layout
    // viewport when the soft keyboard opens instead of shoving fixed content
    // off-screen to "reveal" a focused input. without this, the Fleet
    // Assistant mobile panel's header gets pushed past the top edge when the
    // user taps into the textarea. older browsers ignore unknown keys, so
    // this is a safe progressive enhancement. runtime injection because the
    // dev server may have an older HTML template cached — the static
    // public/index.html has the same hint baked in for prod builds.
    const vp = document.querySelector('meta[name="viewport"]')
    if (vp && !(vp.getAttribute('content') || '').includes('interactive-widget')) {
      const cur = (vp.getAttribute('content') || 'width=device-width, initial-scale=1').replace(/,\s*$/, '')
      vp.setAttribute('content', cur + ', interactive-widget=resizes-content')
    }

    const hashPath = (window.location.hash || '').replace(/^#/, '')
    const routePath = this.$route.fullPath || ''
    const routeBasePath = this.$route.path || ''
    // chromeless routes skip the login redirect below. the list lives in
    // utils/chromeless_routes.js, shared with the build watcher and nudge
    if (isChromelessPath(routePath, hashPath, routeBasePath)) {
      return;
    }

    const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null

    if (user == null || this.$route.name == "login") {
      this.$router.replace({name: "login", params: {nextUrl: this.$route.fullPath}});
    } else {
      let selected_tenant = localStorage.getItem('selected_tenant')
      if (selected_tenant != null) {
        console.log("setting previously selected tenant: " + selected_tenant)
        user.tenant = selected_tenant
      }

      this.set_user_data(user)
      // FI-2180: a tab coming back after days away may hold a jwt that is
      // about to run out; renew it before anything else asks the api
      this.maybeRenewSession()
      this.authenticated = true;
      // FI-1432: kick off the notification polling once we know the user
      // is authenticated on page load. new sessions that arrive via login
      // flip authenticated through setAuthenticated(true) which has its
      // own watcher below.
      this.startNotificationsPolling()
      // sidebar favorites: one fetch per page load, shared by the rail, the
      // page-header stars, and the report dashboard cards
      ensureFavoritesLoaded()
    }
  },
  mounted() {
    // a ticket link clicked anywhere in the app opens the app-level modal
    // (utils/support_ticket_links openSupportTicket). two authenticated
    // branches each mount their own copy; whichever is rendered wins.
    this._openTicketHandler = (ref) => {
      const modal = this.$refs.globalTicketModal
      const target = Array.isArray(modal) ? modal[0] : modal
      if (target && target.open) target.open(ref)
    }
    this.$root.$on('open-support-ticket', this._openTicketHandler)
    // FI-1432: navbar emits 'nc-refresh' through the root bus whenever the
    // profile dropdown opens. run an immediate poll so the badge + list
    // reflect current state instead of waiting up to 60s for the next tick.
    this._ncRefreshHandler = () => {
      if (this.authenticated) this.pollNotifications(true)
    }
    this.$root.$on('nc-refresh', this._ncRefreshHandler)

    // FI-1432: global click delegation for tutorial pills. markdown
    // authored with [Show me](tutorial:id) renders a pill anchor with
    // data-tutorial-id set. one document-level listener covers every
    // surface that renders markdown (announcements, help articles,
    // support tickets, fleet-assistant replies) without requiring each
    // component to wire its own handler.
    this._tutorialClickHandler = (event) => {
      const target = event.target
      if (!target || !target.closest) return
      // follow-up chip: [Label](ask:…) re-asks Fleet Assistant. open the panel
      // prefilled and auto-send so the chip behaves like the user asked it.
      const askEl = target.closest('a[data-fa-ask]')
      if (askEl) {
        event.preventDefault()
        const prompt = askEl.getAttribute('data-fa-ask')
        if (prompt) this.$root.$emit('open-fleet-assistant', { initialQuery: prompt, autoSend: true })
        return
      }
      // action button: [Label](action:NAME) -> an allowlisted operation.
      const actionEl = target.closest('a[data-fa-action]')
      if (actionEl) {
        event.preventDefault()
        this.handleFaAction(actionEl.getAttribute('data-fa-action'), actionEl.getAttribute('data-fa-args') || '', actionEl)
        return
      }
      // tutorial pill: [Show me](tutorial:id).
      const el = target.closest('[data-tutorial-id]')
      if (!el) return
      event.preventDefault()
      const id = el.getAttribute('data-tutorial-id')
      if (!id) return
      launchTutorial(id, { router: this.$router, root: this.$root, isTbn: this.isTbnTenant() })
    }
    document.addEventListener('click', this._tutorialClickHandler)
    // FI-2180: networking/index.js raises this when a 403 stands even after
    // the session was checked with the api, i.e. a real permission denial.
    // the reader stays where they are (no more bounce to login) and gets a
    // gentle word instead.
    this._authDeniedHandler = () => {
      this.showAlert('You do not have access to that page or action. If you think you should, contact support.', 'warning', 8000)
    }
    window.addEventListener('fi-auth-denied', this._authDeniedHandler)
    // FI-1432: NO visibilitychange handler. previously we paused the
    // 60s polling setInterval while the tab was hidden and primed an
    // immediate fetch on resume. that path turned out to be a footgun:
    // some chromium contexts (CDP-driven previews, certain
    // extensions, OS-level focus dances) fire visibilitychange at
    // ~1 Hz, and our resume-prime fired one count+list pair on each
    // event. the 60s setInterval would also be reset before it could
    // expire, so the only requests landing were the rapid primes.
    // chrome already throttles setInterval in hidden tabs to 1/min
    // — same as our intended cadence — so the visibility branch was
    // solving a problem that doesn't exist. left out by design.
  },
  beforeDestroy() {
    if (this._openTicketHandler) this.$root.$off('open-support-ticket', this._openTicketHandler)
    this.stopNotificationsPolling()
    this._stopSessionRenewTimer()
    if (this._authDeniedHandler) {
      window.removeEventListener('fi-auth-denied', this._authDeniedHandler)
      this._authDeniedHandler = null
    }
    if (this._ncRefreshHandler) {
      this.$root.$off('nc-refresh', this._ncRefreshHandler)
      this._ncRefreshHandler = null
    }
    if (this._tutorialClickHandler) {
      document.removeEventListener('click', this._tutorialClickHandler)
      this._tutorialClickHandler = null
    }
  },
  watch: {
    // chromeless standalone boards (/vsm*, /status*) always render the
    // default light theme: the fi-hz shell isn't present there, so a
    // persisted midnight choice only leaks its css VARIABLES into the
    // var(--fi-*) consumers (cards, toolbars) while everything else stays
    // light -- a half-and-half mess. restore the stored theme on the way
    // back into the chromed app.
    '$route.fullPath': {
      immediate: true,
      handler(path) {
        const standalone = !!path && (path.startsWith('/vsm') || path.startsWith('/status'))
        if (standalone) {
          document.documentElement.setAttribute('data-fi-theme', 'original')
          return
        }
        try {
          const t = localStorage.getItem('fi_theme')
          document.documentElement.setAttribute('data-fi-theme', (t === 'new' || t === 'midnight') ? t : 'original')
        } catch (e) {
          document.documentElement.setAttribute('data-fi-theme', 'original')
        }
      },
    },
    authenticated(newVal) {
      if (newVal) {
        fsSetUser(this.$store.state.profile.user)
        this.startNotificationsPolling()
        this.maybeRenewSession()
        ensureFavoritesLoaded()
      } else {
        this.stopNotificationsPolling()
      }
    },
  },
  // mounted() {
  //   console.log("mounted: " + this.$route.fullPath)
  //   const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null
  //   console.log("mounted3: user=" + user)
  //   this.set_user_data(user)
  //
  //   if (!user || this.$route.name != "login") {
  //     console.log("mounted4: login redirect")
  //     this.$router.replace({name: "login", params: {nextUrl: this.$route.fullPath}});
  //   } else {
  //     this.authenticated = true;
  //   }
  // },
  methods: {
    setAuthenticated(status) {
      this.authenticated = status;
      if (!status) {
        this.logout();
      }
    },
    logout() {
      // FI-1860: drop the FullStory identity before clearing storage so the
      // next user on this shared browser starts a fresh, unassociated session.
      fsAnonymize()
      // FI-2182: the per-browser passkey markers ride across the clear, as
      // they do in login.vue created(). a nav sign-out lands here before the
      // login page mounts, and the prompt on the next arrival depends on them.
      const passkeyMarkers = readPasskeyMarkers(localStorage)
      localStorage.clear()
      restorePasskeyMarkers(localStorage, passkeyMarkers)
    },
    // execute a Fleet Assistant action button. the FA_ACTION_ALLOWLIST is the
    // security gate: unknown names no-op, role mismatches disable the control,
    // args are filtered to the declared keys, and a confirm-bearing action
    // prompts before running. see the allowlist comment at the top of this file.
    async handleFaAction(name, argString, anchorEl) {
      const action = FA_ACTION_ALLOWLIST[name]
      if (!action) return // unknown / disallowed -> inert
      const role = this.user && this.user.role
      if (action.roles && action.roles.indexOf(role) === -1) {
        if (anchorEl) anchorEl.setAttribute('aria-disabled', 'true')
        return
      }
      const params = new URLSearchParams(argString || '')
      const args = {}
      ;(action.args || []).forEach((key) => { if (params.has(key)) args[key] = params.get(key) })
      if (action.confirm && !window.confirm(action.confirm)) return
      // navigation actions run synchronously and never throw fatally. operations
      // (action.async) return a promise: show a busy state on the control while
      // it runs and a success/error toast when it settles, so the user gets
      // feedback for work that isn't an immediate page change.
      if (!action.async) {
        try { action.run(args, this) } catch (_) { /* nav failures are non-fatal */ }
        return
      }
      if (anchorEl) { anchorEl.setAttribute('aria-busy', 'true'); anchorEl.classList.add('is-busy') }
      try {
        const msg = await action.run(args, this)
        this.showAlert(msg || action.successMessage || 'Done.', 'success', 4000)
      } catch (e) {
        this.showAlert(action.errorMessage || 'That action could not be completed.', 'danger', 6000)
      } finally {
        if (anchorEl) { anchorEl.removeAttribute('aria-busy'); anchorEl.classList.remove('is-busy') }
      }
    },
    // the app-level ticket modal closed: tell any list or board underneath
    // to refetch that one ticket (they patch their own row / card)
    onGlobalTicketHidden(ref) {
      if (ref) this.$root.$emit('support-ticket-changed', ref)
    },
    showAlert(msg, variant, autoclose) {
      if (this._alertTimeout) clearTimeout(this._alertTimeout)
      this.alert.show = true
      this.alert.message = msg
      this.alert.variant = variant
      this._alertTimeout = autoclose
          ? setTimeout(() => this.closeAlert(), autoclose)
          : null
    },
    closeAlert() {
      this.alert.show = false
      this.alert.message = ''
      this.alert.variant = ''
    },
    ...mapMutations('profile', [ 'set_user_data' ]),
    // FI-1432 notification polling. a single 60s interval drives unread
    // count; every second tick (120s) we also fetch the full list so an
    // open dropdown has fresh rows. list fetch errors are swallowed
    // silently — next tick retries.
    isTbnTenant() {
      try {
        const tenant = this.user && this.user.tenant
        const types = this.user && this.user.types
        return tenant && types && types[tenant] === 'TBN'
      } catch (_) { return false }
    },
    // FI-2180: renew the jwt while it is still valid so a long-lived tab
    // never gets signed out by a release or by its token quietly running
    // out. the attempt stamp is written BEFORE the call so several tabs of
    // the same browser do not all ask at once. success rewrites the stored
    // session and the vuex user (tenant selection kept, theme and favorites
    // untouched); a 401 with error_code 3|4 means the session is over, so
    // the reader goes to login with the way back remembered; anything else
    // is logged and left for the next check.
    async maybeRenewSession() {
      let jwt = null
      let lastAttemptAt = null
      try {
        jwt = localStorage.getItem('jwt')
        lastAttemptAt = localStorage.getItem(RENEW_ATTEMPT_KEY)
      } catch (_) { return }
      const nowMs = Date.now()
      // FI-2273: the decisions live in utils/session_renewal.js (unit tested)
      const step = renewalPrecheck({ jwt, authenticated: this.authenticated, lastAttemptAt, nowMs })
      if (step === 'signed-out') {
        this._signedOutElsewhere()
        return
      }
      if (step === 'end-expired') {
        this._sessionOverToLogin('expired')
        return
      }
      if (step !== 'renew') return
      try { localStorage.setItem(RENEW_ATTEMPT_KEY, nowMs) } catch (_) { /* private mode */ }
      const storedJwt = () => { try { return localStorage.getItem('jwt') } catch (_) { return null } }
      try {
        const resp = await refresh_session()
        // FI-2273: a newer sign-in replaced the token while the call was out
        // (another person on a shared pc): its session wins
        if (storedJwt() !== jwt) return
        const payload = resp && resp.value && resp.value.data
        // applyRenewedSession throws on a reply without token and user
        this.set_user_data(applyRenewedSession(localStorage, payload || {}))
      } catch (e) {
        const body = e && e.response && e.response.data
        const action = renewalFailureAction({
          offeredJwt: jwt,
          storedJwt: storedJwt(),
          httpStatus: e && e.response && e.response.status,
          errorCode: body && body.error_code,
          nowMs: Date.now(),
        })
        if (action === 'park') {
          // the api will never extend this token: stop offering it and let it
          // run to its own exp
          try { localStorage.setItem(RENEW_ATTEMPT_KEY, (tokenExp(jwt) || 0) * 1000) } catch (_) { /* private mode */ }
          return
        }
        if (action === 'expired' || action === 'invalid') {
          this._sessionOverToLogin(action)
          return
        }
        if (action === 'stale') return
        // eslint-disable-next-line no-console
        console.log('session renewal skipped: ' + ((e && e.message) || e))
      }
    },
    _onLoginRoute() {
      const name = this.$route && this.$route.name
      return name === 'login' || name === 'login_redirect'
    },
    _sessionOverToLogin(kind) {
      endSessionLocally(localStorage, sessionStorage, kind)
      // already on the login page (#/login/<page>): keep its nextUrl
      if (this._onLoginRoute()) return
      // same shape App.vue created() uses: the login route with the
      // current page as an in-memory param, never a login path itself
      const fullPath = (this.$route && this.$route.fullPath) || ''
      const params = fullPath && !fullPath.startsWith('/login') ? { nextUrl: fullPath } : {}
      const nav = this.$router.replace({ name: 'login', params })
      if (nav && nav.catch) nav.catch(() => {})
    },
    // FI-2273: the shared jwt vanished while this tab was signed in: another
    // tab signed out. land here as a sign-out (no nextUrl into someone else's
    // page, no passkey prompt on arrival), not as an expired session
    _signedOutElsewhere() {
      try { sessionStorage.setItem(SIGNOUT_KEY, '1') } catch (_) { /* private mode */ }
      if (this._onLoginRoute()) return
      const nav = this.$router.replace({ name: 'login' })
      if (nav && nav.catch) nav.catch(() => {})
    },
    _startSessionRenewTimer() {
      if (this._sessionRenewTimer) return
      this._sessionRenewTimer = setInterval(() => {
        if (this.authenticated) this.maybeRenewSession()
      }, SESSION_RENEW_CHECK_MS)
    },
    _stopSessionRenewTimer() {
      if (this._sessionRenewTimer) {
        clearInterval(this._sessionRenewTimer)
        this._sessionRenewTimer = null
      }
    },
    async pollNotifications(includeList) {
      const tenant = this.user && this.user.tenant
      if (!tenant) return
      const tbn = this.isTbnTenant()
      // noAuthRedirect: a quiet tick that 403s must reject here, not bounce
      // the reader to login mid-task (FI-2180)
      const quiet = { noAuthRedirect: true }
      try {
        const countFn = tbn ? tbn_get_notifications_unread_count : cm_get_unread_count
        const countResp = await countFn(tenant, quiet)
        const count = this._extractCount(countResp)
        if (count != null) {
          this.$store.commit('notifications/set_unread_count', count)
        }
      } catch (_) { /* retry next tick */ }
      if (!includeList) return
      try {
        const listFn = tbn ? tbn_get_user_notifications : cm_get_user_notifications
        const listResp = await listFn(tenant, 20, undefined, quiet)
        const items = this._extractItems(listResp)
        if (items) {
          this.$store.commit('notifications/set_notifications', items)
        }
      } catch (_) { /* retry next tick */ }
    },
    // execute_generator(...).next() resolves to {value, done} where
    // value is the axios response. axios wraps the HTTP body at
    // value.data, and our ApiSuccessResponse wraps it AGAIN as
    // {status, message, data: <payload>, ...}. so the real payload
    // typically lives at resp.value.data.data — two layers deeper than
    // the naive resp.data check. walk through both possibilities so the
    // extractors work whether the response is already-unwrapped or
    // double-wrapped.
    _responsePayload(resp) {
      if (!resp) return null
      const axiosData = (resp.value && resp.value.data) || resp.data || resp
      if (!axiosData) return null
      return axiosData.data || axiosData
    },
    _extractCount(resp) {
      const payload = this._responsePayload(resp)
      if (payload && typeof payload.count === 'number') return payload.count
      return null
    },
    _extractItems(resp) {
      const payload = this._responsePayload(resp)
      if (payload && Array.isArray(payload.items)) return payload.items
      return null
    },
    startNotificationsPolling() {
      if (this._notifPollTimer) return
      this._notifPollTicks = 0
      this._wsActive = false
      // prime immediately so the badge is accurate on login
      this.pollNotifications(true)
      this._scheduleNotifPollTimer(POLL_INTERVAL_FAST_MS)
      // FI-2180: the session renewal check rides the same lifecycle
      this._startSessionRenewTimer()
      // FI-1657: start the live-push WebSocket alongside polling. when
      // it connects we drop the timer to 5min; on any close we
      // immediately restore 60s. polling is the always-on safety net so
      // a failed WS attempt is invisible to the user.
      this.startWsConnection()
    },
    stopNotificationsPolling() {
      if (this._notifPollTimer) {
        clearInterval(this._notifPollTimer)
        this._notifPollTimer = null
      }
      this._notifPollTicks = 0
      this._stopSessionRenewTimer()
      this.stopWsConnection()
      this.$store.commit('notifications/set_unread_count', 0)
      this.$store.commit('notifications/set_notifications', [])
    },
    // restart the poll setInterval at the requested cadence, preserving
    // the tick count so the every-other-tick list refresh keeps working.
    _scheduleNotifPollTimer(intervalMs) {
      this._stopNotifPollTimer()
      this._notifPollTimer = setInterval(() => {
        this._notifPollTicks += 1
        // every 2nd tick (~120s at the fast cadence) also refreshes
        // the list, not just the unread count.
        this.pollNotifications(this._notifPollTicks % 2 === 0)
      }, intervalMs)
    },
    _stopNotifPollTimer() {
      if (this._notifPollTimer) {
        clearInterval(this._notifPollTimer)
        this._notifPollTimer = null
      }
    },
    startWsConnection() {
      const url = process.env.VUE_APP_WS_URL
      const jwt = window.localStorage.getItem('jwt')
      if (!url || !jwt) {
        // DEV path or unprovisioned stage: poll-only, no WS.
        return
      }
      if (!this._notifWs) this._notifWs = new NotificationsWs()
      this._notifWs.start(url, jwt, {
        onConnect: () => {
          // healthy WS: stop polling entirely and prime once to catch
          // anything that landed during the connect gap. push signals
          // drive every refresh from here on; nc-refresh (navbar
          // dropdown open) still fires an immediate poll on demand.
          this._wsActive = true
          this._stopNotifPollTimer()
          this.pollNotifications(true)
        },
        onSignal: (payload) => {
          // server says "go fetch": refresh count + list now.
          if (this.authenticated) this.pollNotifications(true)
          // FI-2173: an erm notification doubles as a "something changed"
          // ping for the erm pages, which poll on their own and use this
          // only to shorten the lag (mixins/erm_live_refresh.js)
          if (payload && typeof payload.link === 'string' && payload.link.startsWith('/ops_desk?')) {
            this.$root.$emit('erm-changed', payload)
          }
        },
        onDisconnect: () => {
          // any close (failed open or dropped session): restart 60s
          // polling immediately so the user doesn't sit on stale state
          // while the client retries the WS in the background. WS
          // retries forever with capped backoff; if it reconnects we
          // stop polling again on the next onConnect.
          if (this._wsActive || !this._notifPollTimer) {
            this._wsActive = false
            this._scheduleNotifPollTimer(POLL_INTERVAL_FAST_MS)
          }
        },
      })
    },
    stopWsConnection() {
      if (this._notifWs) {
        this._notifWs.stop()
      }
      this._wsActive = false
    },
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    // FI-1869: horizon owns nav chrome (midnight is horizon's dark variant);
    // site_theme boots from localStorage in the profile store, so this is
    // right on first paint (no flash)
    horizonNav() {
      const t = this.$store.state.profile.site_theme
      return t === 'new' || t === 'midnight'
    },
    // AWS-console-style favorites strip under the top bar; opted into via the
    // "Use Toolbar" checkbox on the site map's Favorites section.
    // the bar component itself re-checks horizonNavActive, but gating the
    // offset here too avoids reserving dead space were this ever reachable
    // outside horizon.
    showFavoritesBar() {
      return this.horizonNav && !!this.$store.state.profile.nav_favorites_bar
    },
    // shared launcher gate (feature flag + role + preference), one source of
    // truth for both shell branches and the horizon bar's fa button
    faLauncherOn() {
      return faLauncherAvailable(this.user, this.$route.name)
    },
    telematics_type() {
      return this.user.telematics_types[this.user.tenant]
    },
    tenant_type() {
      return this.user.types[this.user.tenant]
    },
  }
}
</script>

<style>
/* FI-1869 horizon nav layout: fixed edge-attached chrome (v27: toolbar spans
   the top, sidebar runs to the window edge); the content pane clears it with
   padding/margins. only rendered when site_theme is 'new' or 'midnight';
   classic never mounts these. --fi-hz-top-offset is set on <html> by
   FiFavoritesBar itself while it's mounted (its own height, which now
   varies since its row wraps instead of scrolling), bumping padding-top
   past the bare 52px top-bar height so fi_section_nav.vue's fixed rail,
   which reads the same variable, clears it too. */
.fi-hz-root { padding-top: var(--fi-hz-top-offset, 52px); }
.fi-hz-content {
  min-width: 0;
  margin-left: 264px;
  padding: 16px 18px 20px;
  transition: margin-left var(--fi-motion-quick);
}
.fi-hz-body--rail-collapsed .fi-hz-content { margin-left: 56px; }
@media (max-width: 991px) {
  /* the rail becomes a fixed overlay drawer; content takes the full width.
     the favorites bar still renders here (as one sideways-scrolling row), so
     the offset stays on the variable it publishes -- which falls back to the
     bare top-bar height on its own whenever the bar is off. */
  .fi-hz-content { margin-left: 0 !important; padding: 12px 10px 16px; }
}
/* ~94 pre-horizon page roots stamp their own inline `margin:20px`, which
   predates this pane and doubles the gutter it already pays (measured 38px
   left / 36px top against 18px/16px on the pages built for horizon). zero it
   inside the shell only -- classic has no content pane, so those pages still
   need their own margin there. !important is required to beat the inline
   style. pages that opt in properly (site_map, main_dashboard) already do
   this for themselves with a .fi-hz-content-scoped rule. */
.fi-hz-content [style*="margin:20px"],
.fi-hz-content [style*="margin: 20px"] { margin: 0 !important; }
/* 14 more page roots use the margin-top-only spelling. 13 of the 14 are the page
   root; the exception is main_dashboard's empty-state, which is excluded by
   class so it keeps its spacing. */
.fi-hz-content [style*="margin-top:20px"]:not(.dw-empty),
.fi-hz-content [style*="margin-top: 20px"]:not(.dw-empty) { margin-top: 0 !important; }
/* the four otp/perf report pages spell their classic gutter this way */
.fi-hz-content [style*="margin: 6px 20px 20px 6px"] { margin: 0 !important; }

.bg-info {
  background-color: steelblue !important;
}

/* exclude .btn so Bootstrap/BV buttons rendered as <a> (e.g. b-button :to)
   keep their variant text color. `:where()` contributes zero specificity
   so this selector is (0,0,1) — same as bare `a` — and lets per-class
   overrides like navbar.vue's `.nav-link { color: white !important }` and
   `.profile-dd-btn.btn-outline-secondary { color: #6c757d !important }`
   keep winning on normal specificity rules. Using `a:not(.btn)` directly
   bumps this to (0,1,1), which clobbers those intended overrides. */
a:where(:not(.btn)) { color: var(--fi-link, steelblue) !important; }

.page-item.active .page-link {
  color: white !important;
  background-color: steelblue !important;
}
.btn-secondary { color: white !important; }

.tc-sticky-save {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: transparent;
  padding: 12px 24px;
  z-index: 1000;
  pointer-events: none;
}
.tc-sticky-save .d-flex {
  pointer-events: auto;
  opacity: 0.5;
  transition: opacity 0.2s ease;
}
.tc-sticky-save .d-flex:hover {
  opacity: 1;
}
.tc-save-slide-enter-active {
  transition: transform 0.2s ease-out, opacity 0.2s ease-out;
}
.tc-save-slide-leave-active {
  transition: transform 0.15s ease-in, opacity 0.15s ease-in;
}
.tc-save-slide-enter, .tc-save-slide-leave-to {
  transform: translateY(16px);
  opacity: 0 !important;
}
.tc-unsaved-label {
  color: #856404;
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  padding: 4px 14px;
  border-radius: 4px;
  font-weight: bold;
}

@keyframes tc-dirty-flash {
  0%   { background-color: transparent; }
  15%  { background-color: #fff3cd; }
  60%  { background-color: #fffde7; }
  100% { background-color: transparent; }
}
.tc-dirty-highlight {
  animation: tc-dirty-flash 2.4s ease-out forwards;
}
@keyframes tc-error-flash {
  0%   { background-color: transparent; outline: 2px solid transparent; }
  10%  { background-color: #fff3cd; outline: 2px solid #ffc107; }
  50%  { background-color: #fff3cd; outline: 2px solid #ffc107; }
  100% { background-color: transparent; outline: 2px solid transparent; }
}
.tc-error-highlight {
  animation: tc-error-flash 1.6s ease-out forwards;
  border-radius: 4px;
}

#app {
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  margin-top: 0px;
  background-color: transparent !important;
  /* clip any horizontal overflow at the app boundary so wide tables, rows
     with Bootstrap's -15px margin, or absolutely-positioned elements can't
     push the document past the viewport and expose the body background.
     use `clip` rather than `hidden`: `hidden` forces overflow-y to compute
     to `auto`, turning #app into a vertical scrollport — so on a short page
     the navbar profile popup overflows that short scrollport and forces a
     scrollbar (trying to fill it with `100vh` overshoots ~1px in Safari and
     leaves a persistent scrollbar). `clip` clips the x-axis WITHOUT creating
     a y-scrollport, so popups overflow to the document (already viewport
     height) harmlessly. `hidden` stays first as a fallback for browsers
     without `overflow: clip` (< Safari 16). */
  overflow-x: hidden;
  overflow-x: clip;
}

.container,
.container-fluid,
#app.container-fluid {
  padding-left: 0px !important;
  padding-right: 0px !important;
}

/* notification-icons inline SVG wrapper (FI-1432). used wherever we
   render a custom brand monogram instead of an FA glyph. the SVG sets
   width/height to 1em so it scales with the parent's font-size — same
   sizing convention as FA icons — and inline-flex centers it on the
   text baseline. unscoped because every consumer (navbar, list, detail,
   banner, composer) renders the same wrapper class. */
.ni-svg {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
.ni-svg svg {
  display: block;
}

</style>
